import { Link } from '@tanstack/react-router';
import { Files, StickyNote } from 'lucide-react';
import { useId } from 'react';
import type { Dictionary } from '../../../i18n/en';
import { useI18n } from '../../../i18n/I18nProvider';
import type { SalesDocumentDetail } from '../../../api/types';
import { Money } from '../../../ui/Layout';
import styles from '../Sales.module.css';
import { TaxStatusBadge, taxAxisOf } from '../status';

/**
 * The related-documents bands (sales-notes.md §6; notes-list-detail.md; D-7, D-13). Each is a
 * **full-width band in the page flow** under the journey (AC-T6), never a side column or a tab —
 * the page mounts it between the Tax Authority actions and the verdict, as a sibling `section`.
 *
 * A note shows «المستندات المرجعية» / "Reference documents": each referenced invoice in the order
 * number (link) · Tax Authority identifier · total · document date, and NEVER number → date →
 * amount (AC-T8). An invoice shows «إشعارات على هذه الفاتورة» / "Notes on this invoice": each note
 * with its kind, number (link), ETA state and total, then «المتبقي القابل للتخفيض» / "Still
 * reducible". A document is one or the other, so at most one band shows.
 */
export function RelatedDocuments({
  orgId,
  detail,
}: {
  orgId: string;
  detail: SalesDocumentDetail;
}) {
  if (detail.references.length > 0) return <ReferencesBand orgId={orgId} detail={detail} />;
  if (detail.notes.length > 0) return <NotesBand orgId={orgId} detail={detail} />;
  return null;
}

/** «·» between two fields in a row; decorative, so it is hidden from assistive technology. */
function Sep() {
  return (
    <span className={styles.subtle} aria-hidden="true">
      ·
    </span>
  );
}

/** D-3: a Tax Authority identifier is shown shortened in a list, never the full uuid. */
function shortEtaId(uuid: string | null): string {
  if (!uuid) return '—';
  return uuid.length > 8 ? `${uuid.slice(0, 8)}…` : uuid;
}

/** The note-kind label of a referenced note row (slice-4 keys; export variant named, never bare). */
function noteKindLabel(t: Dictionary, kind: string): string {
  switch (kind) {
    case 'c':
      return t.detail.kindCredit;
    case 'ec':
      return t.detail.kindExportCredit;
    case 'd':
      return t.detail.kindDebit;
    case 'ed':
      return t.detail.kindExportDebit;
    default:
      return kind;
  }
}

function ReferencesBand({ orgId, detail }: { orgId: string; detail: SalesDocumentDetail }) {
  const { t, formatDate, formatDateTime } = useI18n();
  const titleId = useId();
  return (
    <section
      className={styles.card}
      aria-labelledby={titleId}
      data-testid="related-references"
      id="section-references"
    >
      <div className={styles.cardHead}>
        <h2 id={titleId} className={styles.cardTitle}>
          <span className={styles.cardIcon} aria-hidden="true">
            <Files size={16} />
          </span>
          {t.detail.referenceDocuments}
        </h2>
      </div>
      <div className={styles.cardBody}>
        <ul className={styles.relatedList} data-testid="related-references-list">
          {detail.references.map((r) => {
            // M-1: the band shows the document DATE; prefer the calendar issueDate
            // (formatDate parses YYYY-MM-DD only), falling back to the timestamp formatter
            // so a reference known only by its dateTimeIssued never prints a raw ISO string.
            const date = r.issueDate
              ? formatDate(r.issueDate)
              : r.dateTimeIssued
                ? formatDateTime(r.dateTimeIssued)
                : null;
            return (
              <li key={r.id} className={styles.relatedRow} data-testid="related-reference">
                {/* AC-T8: number · Tax Authority identifier · total · document date, in this order. */}
                <Link
                  to="/orgs/$orgId/sales/invoices/$docId"
                  params={{ orgId, docId: r.id }}
                  className={styles.mono}
                  data-testid="related-reference-number"
                >
                  {r.number ?? '—'}
                </Link>
                <Sep />
                <span dir="ltr" className={styles.etaCode} data-testid="related-reference-eta-id">
                  {shortEtaId(r.etaUuid)}
                </span>
                <Sep />
                <Money value={r.totalAmount} currency="EGP" testId="related-reference-total" />
                <Sep />
                <span data-testid="related-reference-date">{date ?? '—'}</span>
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
}

function NotesBand({ orgId, detail }: { orgId: string; detail: SalesDocumentDetail }) {
  const { t } = useI18n();
  const titleId = useId();
  return (
    <section
      className={styles.card}
      aria-labelledby={titleId}
      data-testid="notes-on-invoice"
      id="section-notes"
    >
      <div className={styles.cardHead}>
        <h2 id={titleId} className={styles.cardTitle}>
          <span className={styles.cardIcon} aria-hidden="true">
            <StickyNote size={16} />
          </span>
          {t.detail.notesOnInvoice}
        </h2>
      </div>
      <div className={styles.cardBody}>
        <ul className={styles.relatedList} data-testid="notes-on-invoice-list">
          {detail.notes.map((n) => {
            const axis = taxAxisOf({ status: n.status, etaStatus: n.etaStatus });
            return (
              <li key={n.id} className={styles.relatedRow} data-testid="related-note">
                <span data-testid="related-note-kind">{noteKindLabel(t, n.kind)}</span>
                <Sep />
                <Link
                  to="/orgs/$orgId/sales/invoices/$docId"
                  params={{ orgId, docId: n.id }}
                  className={styles.mono}
                  data-testid="related-note-number"
                >
                  {n.number ?? '—'}
                </Link>
                <Sep />
                {axis ? (
                  <TaxStatusBadge value={axis} testId="related-note-status" />
                ) : (
                  <span data-testid="related-note-status">—</span>
                )}
                <Sep />
                <Money value={n.totalAmount} currency="EGP" testId="related-note-total" />
              </li>
            );
          })}
        </ul>
        {detail.reducible !== null ? (
          <p className={styles.relatedReducible} data-testid="still-reducible">
            <span className={styles.etaLineLabel}>{t.detail.stillReducible}: </span>
            <Money value={detail.reducible} currency="EGP" testId="still-reducible-amount" />
          </p>
        ) : null}
      </div>
    </section>
  );
}
